import { useNavigate } from "react-router-dom";
import type { JudicialRequirement } from "@/mocks/judicialRequirements";
import { getJudicialStateLabel, getJudicialStateColor, getSemaforoColor, getSemaforoBgColor, getPrioridadColor } from "@/mocks/judicialRequirements";

interface JudicialRequirementCardProps {
  requirement: JudicialRequirement;
}

export default function JudicialRequirementCard({ requirement }: JudicialRequirementCardProps) {
  const navigate = useNavigate();

  const diasRestantes = (() => {
    const hoy = new Date("2026-06-10");
    const limite = new Date(requirement.fechaLimite);
    const diff = Math.ceil((limite.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  })();

  const diasLabel = diasRestantes < 0
    ? `Vencido hace ${Math.abs(diasRestantes)} días`
    : diasRestantes === 0
      ? "Vence HOY"
      : `${diasRestantes} días restantes`;

  return (
    <button
      onClick={() => navigate(`/judicial/requerimiento/${requirement.id}`)}
      className="w-full text-left bg-white border border-background-200/70 rounded-xl p-4 hover:border-background-300/80 hover:bg-background-50/50 transition-all duration-200 cursor-pointer group"
    >
      <div className="flex items-start gap-3">
        {/* Semaforo */}
        <div className="flex-shrink-0 pt-0.5">
          <div className={`w-3 h-3 rounded-full ${getSemaforoColor(requirement.semaforo)} shadow-sm`} />
        </div>

        <div className="flex-1 min-w-0">
          {/* Header row */}
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className="text-sm font-semibold text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
              {requirement.tribunal}
            </span>
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium border ${getPrioridadColor(requirement.prioridad)}`}>
              {requirement.prioridad === "critica" ? "Crítica" : requirement.prioridad === "alta" ? "Alta" : requirement.prioridad === "media" ? "Media" : "Baja"}
            </span>
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium border ${getJudicialStateColor(requirement.estado)}`}>
              {getJudicialStateLabel(requirement.estado)}
            </span>
          </div>

          {/* RIT / Causa */}
          <div className="flex items-center gap-2 text-xs text-foreground-500 mb-1.5">
            <span className="flex items-center gap-1">
              <i className="ri-scales-3-line text-foreground-400" />
              RIT {requirement.rit}
            </span>
            {requirement.studentName && (
              <>
                <span className="text-foreground-300">·</span>
                <span className="flex items-center gap-1">
                  <i className="ri-user-line text-foreground-400" />
                  {requirement.studentName}
                </span>
              </>
            )}
          </div>

          {/* Tipo requerimiento */}
          <p className="text-xs text-foreground-600 line-clamp-1 mb-2">
            {requirement.tipoRequerimiento}
          </p>

          {/* Footer row */}
          <div className="flex items-center gap-3 text-xs">
            <span className={`flex items-center gap-1 font-medium ${
              diasRestantes < 0 ? "text-red-600" :
              diasRestantes <= 3 ? "text-amber-600" :
              "text-foreground-500"
            }`}>
              <i className={`${diasRestantes < 0 ? "ri-error-warning-line" : "ri-time-line"}`} />
              {diasLabel}
            </span>

            {requirement.responsableName ? (
              <span className="flex items-center gap-1 text-foreground-400 ml-auto">
                <i className="ri-user-settings-line" />
                {requirement.responsableName}
              </span>
            ) : (
              <span className="flex items-center gap-1 text-red-500 ml-auto">
                <i className="ri-user-unfollow-line" />
                Sin responsable
              </span>
            )}
          </div>
        </div>

        {/* Arrow */}
        <div className="flex-shrink-0 flex items-center pt-1">
          <span className="w-5 h-5 flex items-center justify-center text-foreground-300 group-hover:text-foreground-600 group-hover:translate-x-0.5 transition-all duration-200">
            <i className="ri-arrow-right-s-line" />
          </span>
        </div>
      </div>
    </button>
  );
}